import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { writePersistedWorkspace } from "../../utils/workspacePersistence";
import { useOnboardingGate } from "../../components/OnboardingGate";
import { submitBrandDetailsFromOnboarding } from "../../services/brandService";
import { createCustomerFromOnboarding } from "../../services/customerService";
import useCurrentUser from "../user-management/hooks/useCurrentUser";
import { useTheme } from "../../context/ThemeManager";
import {
  consumeOnboardingChatStream,
  startCrawlStream,
  mapToolPayloadToBusinessProfile,
  type OnboardingBusinessProfile,
  type CrawlProgressState,
  startStarterKnowledge,
} from "./onboardingAgentStream";
import { createAgent } from "../../services/agentService";
import { defaultModelForAgentType } from "../../components/AIAgents/ChatWithAgent/config/modelOptions";
import { useBrandForm } from "../brand/hooks/useBrandForm";
import type { BrandFormData } from "../brand/hooks/useBrandForm";
import { ChipInput } from "../brand/components/ChipInput";
import { LogoUpload } from "../brand/components/LogoUpload";
import { BrandColorPicker } from "../brand/components/BrandColorPicker";
import { mapFormDataToBrandPayload } from "../brand/utils/brandMapper";
import {
  canonicalWebsiteUrl,
  validateBrandName,
  validateWebsiteUrl,
  validatePhoneNumber,
  validateServiceArea,
  validateTagline,
} from "../brand/utils/brandFormValidation";
import { AgentConnectionPrompt } from "../../components/AIAgents/AgentConnectionPrompt";
import { requestReputationManagerSetup } from "../../services/supportService";
import { QuickStartForm, type QuickStartValues } from "./QuickStartForm";
import { AssistantSetupChat } from "./AssistantSetupChat";
import type { BrandDetailsDraft, CreatedBrand, QuickStartInput, QuickStartResult } from "./quickStartOnboarding";
import { createBrand, loadResume, quickStartOnboarding, saveDetailsAndCreateTeam } from "./quickStartOnboarding";
import { colorsFromProfile, sanitizeLogoUrlForSave } from "./brandDetailsFromProfile";
import { getFreshIdToken } from "../../auth/getFreshIdToken";
import { applyStepEvent, finishAllSteps, type AgentStep } from "../../components/AIAgents/ChatWithAgent/components/AgentStepTimeline";
import { LiveAssistantTurn, SetupComposerDock, SetupThread } from "./SetupChatParts";
import { CHAT_COLUMN_CLASS } from "../../components/AIAgents/ChatWithAgent/constants/chatLayout";

const ONBOARDING_AGENT_ID =
  process.env.REACT_APP_ONBOARDING_AGENT_ID || "onboarding-agent-v1";
const ONBOARDING_CUSTOMER_ID =
  process.env.REACT_APP_ONBOARDING_CUSTOMER_ID || "system_onboarding";
const BRAND_LOGO_SRC = "/sagelyn-ai-assistant.svg";
const BRAND_LOGO_FALLBACK_SRC = "/sagelyn-ai-assistant.svg";

/** Mirrors Brand Details → Content Guidelines quick-select CTAs */
const ONBOARDING_BRAND_CTA_OPTIONS = [
  { value: "Call Us", label: "Call Us" },
  { value: "Book Now", label: "Book Now" },
  { value: "Get a Quote", label: "Get a Quote" },
  { value: "Learn More", label: "Learn More" },
  { value: "Get a Free Estimate", label: "Get a Free Estimate" },
  { value: "Visit Website", label: "Visit Website" },
  { value: "Message Us", label: "Message Us" },
];

// Sagelyn AI Assistant entry actions (SGL-141).
const DEMO_URL = "https://www.sagelyn.ai/how-it-works";
const ACTION_BUTTONS = [
  { key: "account", label: "Set up my account", description: "A few quick questions. No website needed.", icon: "rocket_launch" },
  { key: "demo", label: "Show me a product demo", description: "Watch a quick tour of Sagelyn.", icon: "play_circle" },
  { key: "support", label: "Create a support ticket", description: "Tell us the problem — we'll take it from there.", icon: "support_agent" },
  { key: "ask", label: "Ask a question", description: "Ask me anything about Sagelyn.", icon: "chat_bubble" },
] as const;

type EntryAction = (typeof ACTION_BUTTONS)[number]["key"];

type BusinessProfile = OnboardingBusinessProfile;

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  /** Tool calls the assistant made before this reply. */
  steps?: AgentStep[];
}

type StreamStatus =
  | { type: "idle" }
  | { type: "thinking" }
  | { type: "tool"; name: string }
  | { type: "generating" };

/** Product mark next to “Sagelyn” in the page header (matches app shell / login). */
const SagelynHeaderMark: React.FC<{ size?: "sm" | "md" | "lg" }> = ({ size = "md" }) => {
  const dim = size === "lg" ? "h-12 w-12" : size === "md" ? "h-10 w-10" : "h-8 w-8";
  return (
    <img
      src="/sagelyn-circle.png"
      alt="Sagelyn"
      className={`${dim} shrink-0 rounded-full object-contain`}
      onError={(e) => {
        const target = e.currentTarget;
        target.onerror = null;
        target.style.display = "none";
        if (target.parentElement) {
          const fallback = document.createElement("div");
          fallback.className = `${dim} shrink-0 flex items-center justify-center rounded-full bg-[#183b32]`;
          fallback.innerHTML = `<span class="material-icons text-[#e4b372]" style="font-size: ${size === "md" ? "20px" : "16px"}">auto_awesome</span>`;
          target.parentElement.insertBefore(fallback, target);
        }
      }}
    />
  );
};

/** Avatar for onboarding assistant messages (distinct from header brand mark). */
const AssistantAvatar: React.FC<{ size?: "sm" | "md" | "lg" }> = ({ size = "md" }) => {
  const dim = size === "lg" ? "h-12 w-12" : size === "md" ? "h-10 w-10" : "h-8 w-8";
  return (
    <img
      src={BRAND_LOGO_SRC}
      alt="Sagelyn AI assistant"
      onError={(event) => {
        event.currentTarget.src = BRAND_LOGO_FALLBACK_SRC;
      }}
      className={`${dim} shrink-0 rounded-full object-cover ring-1 ring-white/20`}
    />
  );
};

const OnboardingThemeToggle: React.FC<{
  activeTheme: "light" | "dark";
  onToggle: () => void;
}> = ({ activeTheme, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={`Switch to ${activeTheme === "dark" ? "light" : "dark"} mode`}
    className="flex h-9 w-9 items-center justify-center rounded-full text-theme-fg-muted transition hover:bg-surface-ui-hover hover:text-theme-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
  >
    <span className="material-icons text-[19px]">
      {activeTheme === "dark" ? "dark_mode" : "light_mode"}
    </span>
  </button>
);

const OnboardingHeader: React.FC<{
  activeTheme: "light" | "dark";
  onToggleTheme: () => void;
  onBack?: () => void;
  onManual?: () => void;
}> = ({ activeTheme, onToggleTheme, onBack, onManual }) => (
  <div className="shrink-0 border-b border-theme-border bg-theme-background px-6 py-3.5">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-theme-fg-muted transition-colors hover:bg-surface-ui-hover hover:text-theme-fg"
          >
            <span className="material-icons text-[16px]">arrow_back</span>
            Dashboard
          </button>
        )}
        <SagelynHeaderMark size="md" />
        <span className="text-base font-semibold text-brand-primary">Sagelyn</span>
      </div>
      <div className="flex items-center gap-3">
        <OnboardingThemeToggle activeTheme={activeTheme} onToggle={onToggleTheme} />
      </div>
    </div>
  </div>
);

const OnboardingSecureNote: React.FC<{ showEnterHint?: boolean }> = ({
  showEnterHint = false,
}) => (
  <div
    className={`flex items-center px-2 text-theme-fg-muted ${
      showEnterHint ? "justify-between" : "justify-center"
    }`}
  >
    <div className="flex items-center gap-1.5 text-xs font-medium">
      <span className="material-icons text-[14px]">lock</span>
      Your information is secure
    </div>
    {showEnterHint && (
      <span className="hidden text-[11px] font-semibold tracking-wider sm:inline">
        Press Enter to send
      </span>
    )}
  </div>
);

const OnboardingComposer: React.FC<{
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  inputRef: React.RefObject<HTMLInputElement>;
  isStreaming: boolean;
  hasUserMessages: boolean;
  onSubmit: (event: React.FormEvent) => void;
  variant?: "hero" | "thread";
  placeholder?: string;
}> = ({
  input,
  setInput,
  inputRef,
  isStreaming,
  hasUserMessages,
  onSubmit,
  variant = "thread",
  placeholder,
}) => {
  const isHero = variant === "hero";
  return (
    <form
      onSubmit={onSubmit}
      className={`flex w-full flex-col gap-2.5 ${isHero ? "max-w-[590px]" : "mx-auto max-w-3xl"}`}
    >
      <div
        className={`group flex w-full items-center gap-2 rounded-2xl border border-theme-border bg-theme-surface p-1.5 pl-5 shadow-[0_14px_28px_-22px_rgba(0,0,0,0.42)] transition-all duration-300 hover:border-brand-primary focus-within:border-brand-primary focus-within:ring-2 focus-within:ring-brand-primary ${
          isHero ? "min-h-[58px] shadow-[0_30px_60px_-34px_rgba(0,0,0,0.58)]" : ""
        }`}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            placeholder ??
            (isHero
              ? "Copy and paste your website..."
              : hasUserMessages
                ? "Type a message..."
                : "Type your website or reply here...")
          }
          disabled={isStreaming}
          className={`flex-1 border-none bg-transparent text-theme-fg outline-none placeholder:text-theme-fg-muted focus:outline-none focus:ring-0 disabled:opacity-50 ${
            isHero ? "py-2.5 text-[15px]" : "py-2.5 text-[15px]"
          }`}
        />
        <button
          type="submit"
          disabled={!input.trim() || isStreaming}
          className={`flex shrink-0 items-center justify-center rounded-xl transition-all duration-300 ${
            isHero ? "h-10 w-10" : "h-10 w-10"
          } ${
            !input.trim() || isStreaming
              ? "bg-surface-ui-hover text-theme-fg-muted"
              : "bg-brand-primary text-white shadow-sm hover:-translate-y-[1px] hover:bg-brand-primary-hover hover:shadow active:translate-y-0"
          }`}
        >
          <span className="material-icons text-[18px]">
            {isHero ? "arrow_forward" : "send"}
          </span>
        </button>
      </div>
      {!isHero && <OnboardingSecureNote showEnterHint />}
    </form>
  );
};

const OnboardingQuickReplyButton: React.FC<{
  label: string;
  description: string;
  icon: string;
  onClick: () => void;
  active?: boolean;
}> = ({ label, description, icon, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group flex w-full flex-col items-start gap-2.5 rounded-2xl border p-4 text-left shadow-[0_18px_36px_-30px_rgba(0,0,0,0.52)] transition hover:-translate-y-0.5 hover:border-theme-accent-warm hover:shadow-[0_24px_48px_-32px_rgba(0,0,0,0.6)] ${active ? "border-theme-accent-warm bg-surface-ui-hover ring-2 ring-[#C9A86C]/30" : "border-theme-accent-warm/40 bg-theme-surface"}`}
  >
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-surface-ui-hover text-theme-accent-warm">
      <span className="material-icons text-[18px]">{icon}</span>
    </span>
    <span className="flex flex-col">
      <span className="text-[14px] font-semibold leading-tight text-theme-fg">{label}</span>
      <span className="mt-1 text-[11px] font-medium leading-snug text-theme-fg-muted">
        {description}
      </span>
    </span>
  </button>
);

/**
 * Annotated cue pointing users to the hero website input — mirrors the
 * reference "Enter your website URL" callout, themed in Sagelyn gold so it
 * reads correctly in both light and dark mode.
 */
const OnboardingWebsiteCue: React.FC = () => (
  <div
    className="flex items-center gap-2 self-start pl-3 text-left"
    style={{ animation: "onboardingFadeUp 0.8s ease both" }}
  >
    <svg
      width="18"
      height="22"
      viewBox="0 0 18 22"
      fill="none"
      className="-mt-1 shrink-0 text-theme-accent-warm"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Curved body */}
      <path
        d="M14 21 C14 21 4 19 3 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Arrowhead pointing up */}
      <path
        d="M3 8 L1 13 M3 8 L7 13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <span className="text-[12px] font-bold uppercase tracking-[0.14em] text-theme-accent-warm">
      Input your website here
    </span>
    <span className="rounded-full bg-theme-accent-warm px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-theme-charcoal">
      New
    </span>
  </div>
);

const CrawlStep: React.FC<{
  websiteUrl: string;
  idToken: string;
  messages: { role: "user" | "assistant"; content: string }[];
  onComplete: () => void;
  crawlProgress: CrawlProgressState | null;
  crawlPageTitles: string[];
  setCrawlProgress: React.Dispatch<React.SetStateAction<CrawlProgressState | null>>;
  setCrawlPageTitles: React.Dispatch<React.SetStateAction<string[]>>;
  onboardingChatProvider: string;
  customerId: string;
}> = ({ websiteUrl, idToken, onComplete, crawlProgress, crawlPageTitles, setCrawlProgress, setCrawlPageTitles, customerId }) => {
  const [crawlStarted, setCrawlStarted] = React.useState(false);
  const [crawlError, setCrawlError] = React.useState<string | null>(null);
  const [isStreaming, setIsStreaming] = React.useState(false);

  // Auto-start crawl once on mount using the dedicated endpoint
  React.useEffect(() => {
    if (crawlStarted) return;
    setCrawlStarted(true);

    setIsStreaming(true);
    startCrawlStream({
      idToken,
      websiteUrl: websiteUrl || "",
      customerId,
      onCrawlProgress: (state) => {
        setCrawlProgress(state);
        if (state.latestPageTitle) {
          setCrawlPageTitles((prev) =>
            prev.includes(state.latestPageTitle) ? prev : [...prev, state.latestPageTitle]
          );
        }
      },
      onError: () => setCrawlError("Crawl ran into an issue — you can still continue."),
    })
      .catch(() => setCrawlError("Couldn't reach the crawler — you can still continue."))
      .finally(() => setIsStreaming(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-advance when crawl completes or errors
  React.useEffect(() => {
    if (!isStreaming && crawlStarted) {
      const isDone = crawlProgress?.status === "completed" || crawlProgress?.status === "failed" || crawlProgress?.status === "timeout" || crawlError !== null;
      if (isDone) {
        const timer = setTimeout(onComplete, 1800);
        return () => clearTimeout(timer);
      }
    }
  }, [isStreaming, crawlStarted, crawlProgress?.status, crawlError, onComplete]);

  const pct = crawlProgress && crawlProgress.pagesTotal > 0
    ? Math.min(100, Math.round((crawlProgress.pagesCompleted / crawlProgress.pagesTotal) * 100))
    : 0;

  const isDone = crawlProgress?.status === "completed";
  const isFailed = crawlProgress?.status === "failed" || crawlProgress?.status === "timeout" || crawlError;

  return (
    <div className="flex h-screen flex-col bg-[#fafafa]">
      <div className="sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <SagelynHeaderMark size="md" />
          <span className="font-serif text-xl font-semibold text-[#183b32]">Sagelyn</span>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 py-12 sm:px-6">
        {/* Header bubble */}
        <div className="flex items-start gap-3">
          <AssistantAvatar size="md" />
          <div className="mt-0.5 flex flex-col gap-1">
            <span className="pl-1 text-[13px] font-semibold text-[#183b32]">Sagelyn AI Assistant</span>
            <div className="max-w-fit rounded-xl rounded-tl-sm border border-gray-100 bg-white p-4 text-[14px] leading-relaxed text-[#183b32] shadow-sm">
              {isDone
                ? `Done! I've learned from ${crawlProgress!.pagesCompleted} pages on your site. Setting up your agent…`
                : isFailed
                ? "Crawl hit a snag — don't worry, you can still continue and add content later."
                : `Building your AI knowledge base from ${websiteUrl || "your website"}…`}
            </div>
          </div>
        </div>

        {/* Progress card */}
        <div className={`overflow-hidden rounded-2xl border p-6 shadow-sm transition-colors ${isDone ? "border-[#b8d8cb] bg-[#f0f7f4]" : isFailed ? "border-amber-200 bg-amber-50" : "border-gray-100 bg-white"}`}>
          <div className="flex items-center gap-4 mb-5">
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${isDone ? "bg-[#183b32]" : isFailed ? "bg-amber-100" : "bg-[#f0f4f3]"}`}>
              <span className={`material-icons text-[24px] ${isDone ? "text-[#e4b372]" : isFailed ? "text-amber-500" : "text-[#183b32] animate-spin"}`}>
                {isDone ? "auto_awesome" : isFailed ? "warning" : "travel_explore"}
              </span>
            </div>
            <div>
              <h3 className="font-semibold text-[#183b32] text-[15px]">
                {isDone ? "Knowledge base ready" : isFailed ? "Crawl incomplete" : "Reading your website"}
              </h3>
              <p className="text-[12px] text-gray-500 mt-0.5">
                {isDone
                  ? `${crawlProgress!.pagesCompleted} pages saved as training data`
                  : isFailed
                  ? crawlError || "Some pages could not be read"
                  : "Extracting services, FAQs, pricing, and more…"}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          {!isFailed && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[12px] text-gray-500">Pages read</span>
                <span className="text-[12px] font-semibold text-[#183b32]">
                  {crawlProgress ? `${crawlProgress.pagesCompleted} / ${crawlProgress.pagesTotal}` : "Starting…"}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${isDone ? "bg-[#648b7f]" : "bg-[#183b32]"}`}
                  style={{ width: isDone ? "100%" : `${pct}%` }}
                />
              </div>
            </div>
          )}

          {/* Just-learned feed */}
          {crawlPageTitles.length > 0 && (
            <div className="flex flex-col gap-1.5 max-h-[180px] overflow-y-auto">
              {[...crawlPageTitles].reverse().slice(0, 8).map((title, i) => (
                <div key={i} className="flex items-center gap-2 text-[12px] text-gray-600">
                  <span className="material-icons text-[14px] text-[#648b7f]">check_circle</span>
                  <span className="truncate">{title}</span>
                </div>
              ))}
            </div>
          )}

          {!crawlStarted || (!crawlProgress && !crawlError) && (
            <div className="flex items-center gap-2 text-[13px] text-gray-400">
              <span className="material-icons text-[16px] animate-spin">refresh</span>
              Connecting to crawler…
            </div>
          )}
        </div>

        {/* Skip link */}
        {!isDone && (
          <button
            type="button"
            onClick={onComplete}
            className="mx-auto text-[13px] font-medium text-gray-400 transition hover:text-gray-600 underline underline-offset-2"
          >
            Skip — I'll add content later
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * The brand-setup wizard. At module scope on purpose (SGL-343).
 *
 * This used to be declared inside OnboardingAgentPage. A component defined in
 * another component's body is a brand new function on every parent render, so
 * React saw a new element type, unmounted the subtree and mounted a fresh one.
 * The step counter lives in the parent, which made Back and Next precisely the
 * things that destroyed the form state they exist to navigate: the customer
 * returned to an earlier screen and found her own edits replaced by the values
 * crawled from her website.
 *
 * Keep it here. Nesting it again re-introduces silent data loss.
 */
interface BrandManagementWizardProps {
  step: number;
  onStepChange: React.Dispatch<React.SetStateAction<number>>;
  /** Brand saved: the parent resets the wizard and moves onboarding along. */
  onComplete: () => void;
  editedProfile: BusinessProfile | null;
  onboardingCustomerId: string | null;
  idToken: string;
  activeTheme: "light" | "dark";
  handleThemeToggle: () => void;
}

export const BrandManagementWizard: React.FC<BrandManagementWizardProps> = ({
  step,
  onStepChange,
  onComplete,
  editedProfile,
  onboardingCustomerId,
  idToken,
  activeTheme,
  handleThemeToggle,
}) => {
  const navigate = useNavigate();
  const BRAND_WIZARD_STEP_COUNT = 9;
  const [finishError, setFinishError] = useState<string | null>(null);
  const [isSavingBrand, setIsSavingBrand] = useState(false);

  const labelClass = "text-[14px] font-semibold text-theme-fg";
  const optionalClass = "text-xs font-normal text-theme-fg-muted";
  const inputClass =
    "w-full rounded-lg border border-theme-border bg-theme-surface px-3 py-2 text-[14px] text-theme-fg outline-none transition placeholder:text-theme-fg-muted focus:border-brand-primary focus:ring-1 focus:ring-brand-primary";

  const initialBrandFromChat = useMemo((): Partial<BrandFormData> => {
    const ep = editedProfile;
    if (!ep) return {};

    // service_areas from Firecrawl are the best seed; fall back to city+state
    const serviceAreaSeed: string[] =
      Array.isArray(ep.service_areas) && ep.service_areas.length > 0
        ? ep.service_areas
        : ep.city?.trim()
        ? [[ep.city, ep.state].filter(Boolean).join(", ")]
        : [];

    // Measured colours by role, never by position (SGL-223); see colorsFromProfile.
    const colorsSeed = colorsFromProfile(ep.brand_palette, ep.brand_colors);

    return {
      brandName: ep.business_name || "",
      website: canonicalWebsiteUrl(ep.website_url || ""),
      industry: ep.industry || "",
      brandVoice: ep.brand_voice || "",
      tagline: ep.tagline || "",
      phoneNumber: ep.phone_number || "",
      businessType: (ep.business_type as BrandFormData["businessType"]) || "",
      targetAudience: ep.target_audience || "",
      servicesOffered: Array.isArray(ep.services) && ep.services.length ? ep.services : [],
      serviceArea: serviceAreaSeed,
      ...(colorsSeed ? { colors: colorsSeed } : {}),
      ...(ep.logo_url ? { logoUrl: ep.logo_url } : {}),
    };
  }, [editedProfile]);

  // Seeds the form once, at mount. The memo above recomputes when the profile
  // changes, but useBrandForm only reads it as a useState initialiser, so a
  // later recompute is not applied — and must not be: overwriting the form from
  // the crawl after the customer has started typing is SGL-343's symptom, just
  // from the other direction. The parent only reaches this step after the
  // profile is confirmed, so the seed is complete by the time it lands here.
  const { formData, errors, updateField, validateForm, setFormData } =
    useBrandForm(initialBrandFromChat);

  const canProceed = (stepIndex: number): boolean => {
    switch (stepIndex) {
      case 0:
        return validateBrandName(formData.brandName).isValid;
      case 1:
        return formData.businessType === "SERVICE" ||
          formData.businessType === "PRODUCT" ||
          formData.businessType === "BOTH";
      case 2:
        return (
          validateWebsiteUrl(formData.website).isValid &&
          validatePhoneNumber(formData.phoneNumber).isValid
        );
      case 3:
        return validateServiceArea(formData.serviceArea).isValid;
      case 4: {
        const bt = formData.businessType;
        if (bt === "SERVICE") return formData.servicesOffered.length > 0;
        if (bt === "PRODUCT") return formData.productsOffered.length > 0;
        if (bt === "BOTH")
          return (
            formData.servicesOffered.length > 0 || formData.productsOffered.length > 0
          );
        return false;
      }
      case 5:
        return validateTagline(formData.tagline).isValid;
      case 6:
        return true;
      case 7:
        // Optional (SGL-448): empty is fine; if written, at least 10 characters.
        return formData.brandVoice.trim().length === 0 || formData.brandVoice.trim().length >= 10;
      case 8:
        return true;
      default:
        return true;
    }
  };

  // Why Next is disabled, in the owner's words. A greyed button with no
  // reason is where a paste-from-Google website URL left a real customer
  // stuck on step 3 (2026-09-20).
  const blockReason = (stepIndex: number): string | null => {
    if (canProceed(stepIndex)) return null;
    switch (stepIndex) {
      case 0:
        return validateBrandName(formData.brandName).error || "Add the brand name to continue.";
      case 1:
        return "Choose whether you offer services, products, or both.";
      case 2: {
        const site = validateWebsiteUrl(formData.website);
        if (!site.isValid) return site.error || "Enter a valid website address.";
        return validatePhoneNumber(formData.phoneNumber).error || "Check the phone number.";
      }
      case 3:
        return validateServiceArea(formData.serviceArea).error || "Add at least one service area.";
      case 4:
        return formData.businessType === "PRODUCT"
          ? "Add at least one product."
          : "Add at least one service.";
      case 5:
        return validateTagline(formData.tagline).error || "Shorten the tagline to continue.";
      case 7:
        return "Write at least 10 characters, or leave brand voice empty and add it later.";
      default:
        return "Complete this step to continue.";
    }
  };

  const handleNext = () => {
    if (step < BRAND_WIZARD_STEP_COUNT - 1 && canProceed(step)) {
      onStepChange((s) => s + 1);
    }
  };

  const handleBack = () => onStepChange((s) => Math.max(0, s - 1));

  const handleFinish = async () => {
    setFinishError(null);
    if (!validateForm()) {
      setFinishError(
        "Please fix the fields above — required rules match Brand Management."
      );
      return;
    }

    setIsSavingBrand(true);
    try {
      const answers = mapFormDataToBrandPayload(formData);
      const brandCustomerId = onboardingCustomerId ?? ONBOARDING_CUSTOMER_ID;
      if (idToken) {
        await submitBrandDetailsFromOnboarding(
          brandCustomerId,
          answers as Record<string, unknown>,
          idToken,
          null
        );
      }
      onComplete();
    } catch {
      setFinishError("Could not save brand details. Please try again or finish later in Brand Management.");
    } finally {
      setIsSavingBrand(false);
    }
  };

  const stepBubbles = [
    "Let's match your Brand Management profile — starting with the name that should appear on your content.",
    "How do you operate? This mirrors the business type field on the brand form.",
    "Website and phone — both optional. A website helps your AI employees learn your business.",
    "Where do you serve customers? Add one or more cities or regions like on the main brand form.",
    "List the services and/or products you offer. Requirements follow your business type.",
    "Optional tagline — you can skip if you prefer.",
    "Logo and brand colors — the same upload and palette controls as Brand Management.",
    "Brand voice (required, min. 10 characters) and who you serve.",
    "Calls to action and optional wording guardrails — same as Content Guidelines on the brand page.",
  ];

  const renderNav = (options: { showBack: boolean; isLast: boolean }) => (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
      {!options.isLast && blockReason(step) && (
        <p className="w-full text-[13px] text-amber-700" role="status">
          {blockReason(step)}
        </p>
      )}
      {options.showBack ? (
        <button
          type="button"
          onClick={handleBack}
          className="rounded-lg border border-theme-border bg-theme-surface px-5 py-2 text-[14px] font-medium text-theme-fg shadow-sm transition hover:bg-surface-ui-hover"
        >
          &lt; Back
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={() => navigate("/customer")}
        className="flex items-center gap-1.5 text-[13px] font-medium text-theme-fg-muted transition hover:text-theme-fg"
      >
        <span className="material-icons text-[15px]">edit_note</span>
        I'll do it manually
      </button>
      <div className="flex flex-wrap justify-end gap-2">
        {!options.isLast ? (
          <button
            type="button"
            onClick={handleNext}
            disabled={!canProceed(step)}
            className="rounded-lg bg-brand-primary px-5 py-2 text-[14px] font-medium text-white shadow-sm transition hover:bg-brand-primary-hover disabled:opacity-50"
          >
            Next &gt;
          </button>
        ) : (
          <button
            type="button"
            onClick={handleFinish}
            disabled={isSavingBrand}
            className="rounded-lg bg-brand-primary px-5 py-2 text-[14px] font-medium text-white shadow-sm transition hover:bg-brand-primary-hover disabled:opacity-50"
          >
            {isSavingBrand ? "Saving…" : "Complete setup"}
          </button>
        )}
      </div>
    </div>
  );

  const renderStepCard = () => {
    switch (step) {
      case 0:
        return (
          <div className="flex flex-col gap-3 p-5">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>
                Brand Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.brandName}
                onChange={(e) => updateField("brandName", e.target.value)}
                className={inputClass}
                placeholder="e.g. Acme Corporation"
                maxLength={50}
              />
              {errors.brandName && (
                <p className="text-xs text-red-600">{errors.brandName}</p>
              )}
            </div>
            {renderNav({ showBack: false, isLast: false })}
          </div>
        );
      case 1:
        return (
          <div className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>
                Business type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.businessType}
                onChange={(e) =>
                  setFormData({
                    businessType: e.target.value as BrandFormData["businessType"],
                  })
                }
                className={inputClass}
              >
                <option value="">Select business type</option>
                <option value="SERVICE">Service</option>
                <option value="PRODUCT">Product</option>
                <option value="BOTH">Both</option>
              </select>
              {errors.businessType && (
                <p className="text-xs text-red-600">{errors.businessType}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>
                Industry <span className={optionalClass}>(recommended)</span>
              </label>
              <input
                type="text"
                value={formData.industry}
                onChange={(e) => updateField("industry", e.target.value)}
                className={inputClass}
                placeholder="e.g. Carpet Cleaning, Med Spa, Roofing..."
              />
            </div>
            {renderNav({ showBack: true, isLast: false })}
          </div>
        );
      case 2:
        return (
          <div className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>
                Website URL <span className="text-theme-fg-muted font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={formData.website}
                onChange={(e) => updateField("website", e.target.value)}
                className={inputClass}
                placeholder="https://www.example.com or www.example.com"
              />
              {errors.website && (
                <p className="text-xs text-red-600">{errors.website}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>
                Phone <span className={optionalClass}>(optional)</span>
              </label>
              <input
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => updateField("phoneNumber", e.target.value)}
                className={inputClass}
                placeholder="+1 (555) 123-4567"
              />
              {errors.phoneNumber && (
                <p className="text-xs text-red-600">{errors.phoneNumber}</p>
              )}
            </div>
            {renderNav({ showBack: true, isLast: false })}
          </div>
        );
      case 3:
        return (
          <div className="flex flex-col gap-3 p-5">
            <ChipInput
              label="Service area *"
              chips={formData.serviceArea}
              onChipsChange={(areas) => setFormData({ serviceArea: areas })}
              placeholder="Type a city or region and press Enter"
              helperText="Same chip list as Brand Details — add every area you cover."
            />
            {errors.serviceArea && (
              <p className="text-xs text-red-600">{errors.serviceArea}</p>
            )}
            {renderNav({ showBack: true, isLast: false })}
          </div>
        );
      case 4:
        return (
          <div className="flex flex-col gap-4 p-5">
            {(formData.businessType === "SERVICE" ||
              formData.businessType === "BOTH" ||
              !formData.businessType) && (
              <ChipInput
                label={
                  formData.businessType === "SERVICE" ||
                  formData.businessType === "BOTH"
                    ? "Services offered *"
                    : "Services offered"
                }
                chips={formData.servicesOffered}
                onChipsChange={(chips) => setFormData({ servicesOffered: chips })}
                placeholder="Type a service and press Enter"
              />
            )}
            {errors.servicesOffered && (
              <p className="text-xs text-red-600">{errors.servicesOffered}</p>
            )}
            {(formData.businessType === "PRODUCT" || formData.businessType === "BOTH") && (
              <ChipInput
                label={
                  formData.businessType === "PRODUCT" || formData.businessType === "BOTH"
                    ? "Products offered *"
                    : "Products offered"
                }
                chips={formData.productsOffered}
                onChipsChange={(chips) => setFormData({ productsOffered: chips })}
                placeholder="Type a product and press Enter"
              />
            )}
            {errors.productsOffered && (
              <p className="text-xs text-red-600">{errors.productsOffered}</p>
            )}
            {renderNav({ showBack: true, isLast: false })}
          </div>
        );
      case 5:
        return (
          <div className="flex flex-col gap-3 p-5">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>
                Tagline <span className={optionalClass}>(optional)</span>
              </label>
              <textarea
                value={formData.tagline}
                onChange={(e) => updateField("tagline", e.target.value)}
                rows={2}
                maxLength={120}
                className={`${inputClass} resize-none`}
                placeholder="Short phrase customers remember"
              />
              {errors.tagline && (
                <p className="text-xs text-red-600">{errors.tagline}</p>
              )}
            </div>
            {renderNav({ showBack: true, isLast: false })}
          </div>
        );
      case 6:
        return (
          <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto p-5">
            <LogoUpload
              logoUrl={formData.logoUrl}
              logoMediaId={formData.logoMediaId}
              onLogoChange={(url, mediaId) => {
                const cleanLogoUrl = sanitizeLogoUrlForSave(url);
                setFormData({ logoUrl: cleanLogoUrl, logoMediaId: mediaId });
              }}
              error={errors.logoUrl}
              customerId={onboardingCustomerId ?? ONBOARDING_CUSTOMER_ID}
            />
            {/* Detected colours are labelled as detected (SGL-223). A colour the
                customer has confirmed is declared data; one we inferred and saved
                quietly is the defect this ticket is about. */}
            {formData.colors.length > 0 && editedProfile?.brand_colors_detected && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Colors detected from your website. Please review and adjust before saving.
              </div>
            )}
            <BrandColorPicker
              colors={formData.colors}
              onColorsChange={(colors) => setFormData({ colors })}
              maxColors={4}
            />
            {renderNav({ showBack: true, isLast: false })}
          </div>
        );
      case 7:
        return (
          <div className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>
                Brand voice <span className="text-theme-fg-muted font-normal">(optional)</span>
              </label>
              <textarea
                value={formData.brandVoice}
                onChange={(e) => updateField("brandVoice", e.target.value)}
                rows={3}
                className={`${inputClass} resize-none`}
                placeholder="e.g. Friendly, direct, no jargon — min. 10 characters"
              />
              {errors.brandVoice && (
                <p className="text-xs text-red-600">{errors.brandVoice}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>
                Target audience <span className={optionalClass}>(optional)</span>
              </label>
              <textarea
                value={formData.targetAudience}
                onChange={(e) => updateField("targetAudience", e.target.value)}
                rows={2}
                className={`${inputClass} resize-none`}
                placeholder="Who you help — regions, roles, needs..."
              />
            </div>
            {renderNav({ showBack: true, isLast: false })}
          </div>
        );
      case 8:
        return (
          <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto p-5">
            <div>
              <label className={`mb-2 block ${labelClass}`}>
                Call-to-actions <span className={optionalClass}>(optional)</span>
              </label>
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-theme-border bg-theme-background p-3">
                {ONBOARDING_BRAND_CTA_OPTIONS.map((option) => {
                  const current = Array.isArray(formData.primaryCTA) ? formData.primaryCTA : [];
                  return (
                    <label
                      key={option.value}
                      className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm text-theme-fg-muted transition hover:bg-surface-ui-hover hover:text-theme-fg"
                    >
                      <input
                        type="checkbox"
                        checked={current.includes(option.label)}
                        onChange={(e) => {
                          let next: string[];
                          if (e.target.checked) {
                            next = current.includes(option.label)
                              ? current
                              : [...current, option.label];
                          } else {
                            next = current.filter((c) => c !== option.label);
                          }
                          setFormData({ primaryCTA: Array.from(new Set(next)) });
                        }}
                        className="h-4 w-4 rounded border-theme-border text-brand-primary focus:ring-brand-primary"
                      />
                      {option.label}
                    </label>
                  );
                })}
              </div>
              <div className="mt-3">
                <ChipInput
                  label="Custom CTAs"
                  chips={Array.isArray(formData.primaryCTA) ? formData.primaryCTA : []}
                  onChipsChange={(ctas) =>
                    setFormData({ primaryCTA: Array.from(new Set(ctas)) })
                  }
                  placeholder="Type a CTA and press Enter"
                />
              </div>
            </div>
            <ChipInput
              label="Avoid these words or phrases (optional)"
              chips={formData.avoidWords}
              onChipsChange={(words) => setFormData({ avoidWords: words })}
              placeholder="Press Enter to add"
            />
            <ChipInput
              label="Must include phrases (optional)"
              chips={formData.mustIncludeWords}
              onChipsChange={(words) => setFormData({ mustIncludeWords: words })}
              placeholder="Press Enter to add"
            />
            {finishError && (
              <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {finishError}
              </p>
            )}
            {renderNav({ showBack: true, isLast: true })}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-y-auto bg-theme-background text-theme-fg">
      <div className="sticky top-0 z-10 shrink-0 border-b border-theme-border bg-theme-background px-6 py-3 shadow-sm">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
          <div className="flex items-center gap-3 justify-self-start">
            <SagelynHeaderMark size="md" />
            <span className="font-serif text-xl font-semibold text-brand-primary">Sagelyn</span>
          </div>
          <span className="justify-self-center rounded-full border border-theme-border bg-theme-surface px-3 py-1 text-xs font-medium text-theme-fg-muted">
            Brand setup {step + 1}/{BRAND_WIZARD_STEP_COUNT}
          </span>
          <div className="justify-self-end">
            <OnboardingThemeToggle activeTheme={activeTheme} onToggle={handleThemeToggle} />
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-6">
        {stepBubbles.slice(0, step + 1).map((bubble, i) => (
          <div
            key={i}
            className="flex flex-col gap-3 duration-500 animate-in fade-in slide-in-from-bottom-4"
          >
            <div className="flex items-start gap-3">
              <AssistantAvatar size="md" />
              <div className="mt-0.5 flex flex-col gap-1">
                <span className="pl-1 text-[13px] font-semibold text-theme-fg">
                  Sagelyn Setup Assistant
                </span>
                <div className="max-w-fit rounded-xl rounded-tl-sm border border-theme-border bg-theme-surface p-3.5 text-[14px] leading-relaxed text-theme-fg shadow-sm">
                  {bubble}
                </div>
              </div>
            </div>
            {i === step && (
              <div className="ml-[52px] max-w-2xl overflow-hidden rounded-xl border border-theme-border bg-theme-surface shadow-sm">
                {renderStepCard()}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const OnboardingAgentPage: React.FC = () => {
  const { idToken, userId } = useAuth();
  const { markOnboardingComplete } = useOnboardingGate();
  const { user, loading: userLoading } = useCurrentUser();
  const { setTheme, activeTheme } = useTheme();
  const navigate = useNavigate();

  const [onboardingStep, setOnboardingStep] = useState<"chat" | "brand_management" | "agent_selection" | "reveal">("chat");
  const [onboardingCustomerId, setOnboardingCustomerId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const messagesRef = useRef<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  // Which entry action the user picked on the hero screen (SGL-141): "account"
  // shows the website prompt + cue, "ask" switches to a free-question prompt.
  const [entryMode, setEntryMode] = useState<EntryAction>("account");
  // A Brand left after step 1 (SGL-512): OnboardingGate sends the owner here
  // with ?brand=<id>, and setup opens on step 2 for it. undefined = loading.
  const resumeBrandId = useMemo(() => new URLSearchParams(window.location.search).get("brand"), []);
  const [resume, setResume] = useState<{ brand: CreatedBrand; values: QuickStartValues } | null | undefined>(
    resumeBrandId ? undefined : null
  );
  useEffect(() => {
    if (!resumeBrandId || !idToken) return;
    let cancelled = false;
    loadResume(resumeBrandId, idToken)
      .then((r) => !cancelled && setResume(r))
      .catch((e) => {
        console.error("[SGL-512] Couldn't load the Brand to finish setup; starting fresh:", e);
        if (!cancelled) setResume(null);
      });
    return () => {
      cancelled = true;
    };
  }, [resumeBrandId, idToken]);
  // Form-first onboarding (SGL-448). `assistantMode` opts into the old
  // read-my-website chat; `quickFormPrefill` opens the form full screen
  // (from "I'll do it manually"), pre-filled with anything already found.
  const [assistantMode, setAssistantMode] = useState(false);
  const [quickFormPrefill, setQuickFormPrefill] = useState<Partial<QuickStartValues> | null>(null);
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [quickError, setQuickError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>({ type: "idle" });
  const [streamingContent, setStreamingContent] = useState("");
  const [liveSteps, setLiveSteps] = useState<AgentStep[]>([]);
  // The setup chat starts centred (agent chat's new-chat view) and docks its
  // input at the bottom once the owner sends a first message.
  const [setupStarted, setSetupStarted] = useState(false);
  const liveStepsRef = useRef<AgentStep[]>([]);
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(null);
  const [editedProfile, setEditedProfile] = useState<BusinessProfile | null>(null);
  const [editingField, setEditingField] = useState<keyof BusinessProfile | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [crawlProgress, setCrawlProgress] = useState<CrawlProgressState | null>(null);
  const [crawlPageTitles, setCrawlPageTitles] = useState<string[]>([]);
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [agentCreationError, setAgentCreationError] = useState<string | null>(null);
  const [selectedAgentTitle, setSelectedAgentTitle] = useState<string>("");
  const [createdAgentId, setCreatedAgentId] = useState<string | null>(null);
  const [trainingStatus, setTrainingStatus] = useState<"idle" | "running" | "done" | "error">("idle");
  const [chatTransitionActive, setChatTransitionActive] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  // Reputation Manager setup request (SGL-141 companion): ask whether they
  // already have GoHighLevel before notifying the team, so the team knows
  // whether to connect an existing account or offer the +$50/mo managed-GHL upsell.
  const [ghlAnswer, setGhlAnswer] = useState<"yes" | "no" | null>(null);
  const [ghlRequestStatus, setGhlRequestStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [brandWizardStep, setBrandWizardStep] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const onboardingChatProvider =
    process.env.REACT_APP_ONBOARDING_CHAT_PROVIDER?.trim() || "openai";

  const navigateAfterOnboarding = useCallback(() => {
    if (onboardingCustomerId) {
      navigate(`/?customerId=${encodeURIComponent(onboardingCustomerId)}`);
    } else {
      navigate("/");
    }
  }, [navigate, onboardingCustomerId]);

  const hasUserMessages = messages.some((m) => m.role === "user");

  useEffect(() => {
    if (userLoading) return;

    if (messages.length === 0) {
      const userName = user?.display_name?.split(' ')[0] || 'there';
      const initialGreeting = `Hi ${userName}! 👋\n\nI'll help you set up your account and build your business profile.`;
      setMessages([{ role: "assistant", content: initialGreeting }]);
    }
  }, [user, userLoading, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent, streamStatus, businessProfile]);

  useEffect(() => {
    if (hasUserMessages || businessProfile) {
      setChatTransitionActive(false);
      const rafId = window.requestAnimationFrame(() => {
        setChatTransitionActive(true);
      });
      return () => window.cancelAnimationFrame(rafId);
    }
    setChatTransitionActive(false);
    return undefined;
  }, [hasUserMessages, businessProfile]);

  const sendMessage = useCallback(
    async (userText: string) => {
      if (!idToken || isStreaming) return;

      const newUserMsg: ChatMessage = { role: "user", content: userText };
      const thread = [...messagesRef.current, newUserMsg];
      messagesRef.current = thread;

      setMessages(thread);
      setInput("");
      setIsStreaming(true);
      setStreamStatus({ type: "thinking" });
      setStreamingContent("");
      liveStepsRef.current = [];
      setLiveSteps([]);

      try {
        const { assistantText, mergedProfilePayload, streamError } =
          await consumeOnboardingChatStream({
            idToken,
            agentId: ONBOARDING_AGENT_ID,
            customerId: ONBOARDING_CUSTOMER_ID,
            provider: onboardingChatProvider,
            messages: thread,
            setStreamStatus,
            setStreamingContent,
            onStep: (event) => {
              liveStepsRef.current = applyStepEvent(liveStepsRef.current, event);
              setLiveSteps(liveStepsRef.current);
            },
            onCrawlProgress: (state) => {
              setCrawlProgress(state);
              if (state.latestPageTitle) {
                setCrawlPageTitles((prev) =>
                  prev.includes(state.latestPageTitle) ? prev : [...prev, state.latestPageTitle]
                );
              }
            },
          });

        if (streamError) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                streamError.message ||
                "Something went wrong. Please try again.",
            },
          ]);
          return;
        }

        const assistantBubble =
          assistantText.trim() ||
          "Here's what I found — review and edit below.";

        if (mergedProfilePayload) {
          const profile = mapToolPayloadToBusinessProfile(
            mergedProfilePayload,
            assistantText
          );
          setBusinessProfile(profile);
          setEditedProfile(profile);
        }

        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: assistantBubble, steps: finishAllSteps(liveStepsRef.current) },
        ]);
      } catch (e) {
        const msg =
          e instanceof Error ? e.message : "Unable to reach the assistant.";
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: `Sorry — ${msg}` },
        ]);
      } finally {
        setIsStreaming(false);
        setStreamStatus({ type: "idle" });
        setStreamingContent("");
        liveStepsRef.current = [];
        setLiveSteps([]);
      }
    },
    [idToken, isStreaming, onboardingChatProvider]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;
    sendMessage(input.trim());
  };

  const handleActionButton = (key: EntryAction) => {
    if (key === "demo") {
      // Product tour lives on the marketing site (7-video how-it-works page).
      window.open(DEMO_URL, "_blank", "noopener,noreferrer");
      return;
    }
    // "account" and "ask" both use the composer — switch the prompt + cue and focus it.
    // (A pasted URL runs the profile flow; a plain question gets a conversational reply.)
    setEntryMode(key);
    inputRef.current?.focus();
  };

  const handleThemeToggle = () => {
    setTheme(activeTheme === "dark" ? "light" : "dark");
  };

  const handleBrandWizardComplete = () => {
    setBrandWizardStep(0);
    setOnboardingStep("agent_selection");
  };

  const handleGhlAnswer = async (hasGhl: boolean) => {
    setGhlAnswer(hasGhl ? "yes" : "no");
    if (!idToken) {
      setGhlRequestStatus("error");
      return;
    }
    setGhlRequestStatus("sending");
    try {
      await requestReputationManagerSetup(
        onboardingCustomerId ?? ONBOARDING_CUSTOMER_ID,
        idToken,
        hasGhl
      );
      setGhlRequestStatus("sent");
    } catch (e) {
      console.error("Reputation Manager setup request failed:", e);
      setGhlRequestStatus("error");
    }
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setGhlAnswer(null);
    setGhlRequestStatus("idle");
  };

  const openQuickForm = () => {
    setQuickError(null);
    setQuickFormPrefill({
      businessName: editedProfile?.business_name ?? "",
      city: editedProfile?.city ?? "",
      state: editedProfile?.state ?? "",
      country: editedProfile?.country ?? "",
      website: editedProfile?.website_url ?? "",
      services: editedProfile?.services ?? [],
    });
  };

  const quickInput = (values: QuickStartValues): QuickStartInput => {
    if (!idToken || !userId?.trim()) {
      throw new Error("You're signed out. Please refresh the page and try again.");
    }
    return { ...values, contactEmail: user?.email?.trim() || "", userId: userId.trim() };
  };

  /**
   * Every agent gets the starter knowledge (website pages + icp.md,
   * services.md, faq.md, pricing.md), built on the server so it finishes
   * even if the owner leaves this page. A Brand created moments ago is only
   * in a newly issued token. Setup never waits on or fails because of it.
   */
  const startKnowledgeFor = async (result: QuickStartResult, website: string) => {
    const agentIds = result.agents.map((a) => a.id);
    if (agentIds.length === 0) return;
    try {
      await startStarterKnowledge({
        idToken: await getFreshIdToken(),
        customerId: result.customerId,
        agentIds,
        websiteUrl: website.trim() || undefined,
      });
      result.knowledgeStarted = true;
    } catch (e) {
      console.error("Starter knowledge could not be started:", e);
      result.knowledgeStarted = false;
    }
  };

  /** The form path: Brand, details and agents in one go. Throws only if the
   *  Brand could not be created. */
  const createFromAnswers = async (values: QuickStartValues): Promise<QuickStartResult> => {
    const result = await quickStartOnboarding(quickInput(values), idToken as string);
    await startKnowledgeFor(result, values.website);
    return result;
  };

  /** Chat step 1 (SGL-512): the Brand, plus what the website read found. */
  const createBrandFromAnswers = (values: QuickStartValues, found: BrandDetailsDraft): Promise<CreatedBrand> =>
    createBrand(quickInput(values), found, idToken as string);

  /** Chat step 2: the confirmed details (Brand goes ACTIVE), then the team. */
  const saveDetailsFromAnswers = async (
    brand: CreatedBrand,
    values: QuickStartValues,
    details: BrandDetailsDraft
  ): Promise<QuickStartResult> => {
    const result = await saveDetailsAndCreateTeam(brand, quickInput(values), details);
    await startKnowledgeFor(result, values.website);
    return result;
  };

  const finishOnboarding = (
    result: QuickStartResult,
    destination: "agent" | "connect" | "details" | "edit",
    agentId?: string,
  ) => {
    markOnboardingComplete();
    // Switch the app to the new Brand first. The home page only takes the
    // Brand from the URL when none is selected, so an owner who already had a
    // Brand landed on it instead of the new agent's chat. Onboarding sits
    // outside MainLayout (no workspace context here), so write the persisted
    // selection MainLayout reads when it mounts, as LinkedInCallback does.
    writePersistedWorkspace(result.customerId, result.businessName || null);
    const params = new URLSearchParams({ customerId: result.customerId });
    if (destination === "edit" && agentId) {
      params.set("agentId", agentId);
      navigate(`/edit-agent?${params.toString()}`);
      return;
    }
    if (destination === "details") {
      // The Brand editor: services, customers, voice. A guided chat for these
      // comes next; it needs a merge-safe Brand update first.
      navigate(`/customer?${params.toString()}`);
      return;
    }
    if (destination === "connect") {
      navigate(`/connect?${params.toString()}`);
      return;
    }
    const chatWith = agentId || result.landingAgentId;
    if (chatWith) params.set("agentId", chatWith);
    navigate(`/?${params.toString()}`);
  };

  const handleQuickStart = async (values: QuickStartValues) => {
    setQuickSubmitting(true);
    setQuickError(null);
    try {
      finishOnboarding(await createFromAnswers(values), "agent");
    } catch (e) {
      setQuickError(
        e instanceof Error && e.message
          ? `We couldn't create your profile: ${e.message}`
          : "We couldn't create your profile. Please try again."
      );
    } finally {
      setQuickSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!editedProfile || !idToken) return;

    const email =
      editedProfile.contact_email?.trim() || user?.email?.trim() || "";
    if (!email) {
      setConfirmError(
        "Add a contact email (or ensure your account has an email) before continuing."
      );
      return;
    }
    if (!userId?.trim()) {
      setConfirmError("Missing signed-in user. Please refresh and try again.");
      return;
    }

    setIsConfirming(true);
    setConfirmError(null);

    try {
      const customerId = await createCustomerFromOnboarding(
        {
          business_name: editedProfile.business_name.trim(),
          city: editedProfile.city.trim(),
          state: editedProfile.state.trim(),
          country: editedProfile.country.trim() || "USA",
          contact_person_email: email,
          created_by: userId.trim(),
        },
        idToken
      );
      setOnboardingCustomerId(customerId);
      setBrandWizardStep(0);
      setOnboardingStep("brand_management");
    } catch (e) {
      setConfirmError(
        e instanceof Error
          ? e.message
          : "Failed to create your business profile."
      );
    } finally {
      setIsConfirming(false);
    }
  };

  const AGENT_CONFIGS: Record<string, { agent_type: string; agent_name: string; instructions: string; tools: string[] }> = {
    social_media_manager: {
      agent_type: "content_creator",
      agent_name: "Social Media Manager",
      instructions: "You are a social media manager AI assistant. Help create engaging social media posts, generate images, research trends, and manage content across platforms. Always tailor content to the business's brand voice and target audience.",
      tools: ["output_social_posts", "output_images", "generate_image", "get_posts_with_analytics"],
    },
    booking_agent: {
      agent_type: "communication_agent_sms",
      agent_name: "AI Receptionist",
      instructions: "You are an AI receptionist. Answer customer inquiries, help schedule appointments, check availability, and confirm bookings via SMS. Be friendly, efficient, and always confirm details before booking.",
      tools: ["ghl_get_availability", "ghl_book_appointment"],
    },
  };

  const handleAgentSelect = async (optionId: string) => {
    if (!idToken || !onboardingCustomerId) return;
    const config = AGENT_CONFIGS[optionId];
    if (!config) return;

    setIsCreatingAgent(true);
    setSelectedAgentTitle(config.agent_name);
    setAgentCreationError(null);
    let createdId: string | undefined;
    try {
      // Per-type default (SGL-315): Opus 5 for the Visibility Manager, Sonnet 5 otherwise.
      const defaultOption = defaultModelForAgentType(config.agent_type);
      const defaultModel = defaultOption.model;
      const defaultProvider = defaultOption.provider;

      const result = await createAgent(
        {
          agent_name: config.agent_name,
          instructions: config.instructions,
          model: defaultModel,
          provider: defaultProvider,
          customer_id: onboardingCustomerId,
          agent_type: config.agent_type,
          tools: config.tools,
        },
        idToken
      );
      if (result?.data?.ai_agent_id) {
        createdId = result.data.ai_agent_id;
        setCreatedAgentId(createdId);
      }
    } catch (e: any) {
      if (!e?.isGatewayTimeout) {
        setAgentCreationError("Agent creation failed — you can add agents later from the dashboard.");
      }
    } finally {
      setIsCreatingAgent(false);
      setOnboardingStep("reveal");
      // Build the starter knowledge (website pages + icp/services/faq/pricing
      // files) on the server, attached to the agent just created (SGL-461).
      if (createdId && onboardingCustomerId) {
        try {
          await startStarterKnowledge({
            idToken: await getFreshIdToken(),
            customerId: onboardingCustomerId,
            agentIds: [createdId],
            websiteUrl: editedProfile?.website_url?.trim() || undefined,
          });
        } catch (e) {
          console.error("Starter knowledge could not be started:", e);
          setTrainingStatus("error");
        }
      }
    }
  };

  const handleRetry = () => {
    setBusinessProfile(null);
    setEditedProfile(null);
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: "No problem — share another URL or describe your business and I'll try again.",
      },
    ]);
  };

  const patchProfile = (patch: Partial<BusinessProfile>) =>
    setEditedProfile((prev) => prev ? { ...prev, ...patch } : prev);

  const firstName = user?.display_name?.trim().split(/\s+/)[0] || "there";
  const showWelcome = !hasUserMessages && !businessProfile;


  if (onboardingStep === "brand_management") {
    return (
      <BrandManagementWizard
        step={brandWizardStep}
        onStepChange={setBrandWizardStep}
        onComplete={handleBrandWizardComplete}
        editedProfile={editedProfile}
        onboardingCustomerId={onboardingCustomerId}
        idToken={idToken}
        activeTheme={activeTheme}
        handleThemeToggle={handleThemeToggle}
      />
    );
  }

  if (onboardingStep === "agent_selection") {
    const agentOptions = [
      {
        id: "social_media_manager",
        icon: "campaign",
        title: "Social Media Manager",
        description: "Creates posts, researches trends, generates images, and schedules content across all your platforms.",
        chips: ["Social Posts", "Image Generation", "Trend Research", "Content Calendar"],
        color: "#183b32",
        accent: "#e4b372",
      },
      {
        id: "booking_agent",
        icon: "support_agent",
        title: "AI Receptionist",
        description: "Answers inquiries 24/7, books appointments, and follows up with leads automatically via SMS.",
        chips: ["SMS Replies", "Appointment Booking", "Lead Follow-up", "24/7 Coverage"],
        color: "#1a3a5c",
        accent: "#7eb8f7",
      },
    ];

    if (isCreatingAgent) {
      const creationSteps = [
        { icon: "settings", label: `Configuring your ${selectedAgentTitle}` },
        { icon: "tune", label: "Setting up tools and capabilities" },
        { icon: "auto_awesome", label: "Applying your brand context" },
        { icon: "check_circle", label: "Almost ready…" },
      ];
      return (
        <div className="flex h-screen flex-col bg-theme-background text-theme-fg">
          <OnboardingHeader activeTheme={activeTheme} onToggleTheme={handleThemeToggle} />
          <div className="flex flex-1 items-center justify-center px-4">
            <div className="flex animate-in fade-in flex-col items-center gap-6 text-center duration-500">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary shadow-md">
                <span className="material-icons animate-spin text-[32px] text-theme-accent-warm">refresh</span>
              </div>
              <div className="flex flex-col gap-1">
                <h2 className="font-serif text-2xl font-bold text-theme-fg">Setting up your AI assistant</h2>
                <p className="text-[14px] text-theme-fg-muted">This takes just a moment…</p>
              </div>
              <div className="flex w-full max-w-sm flex-col gap-3 rounded-2xl border border-theme-border bg-theme-surface p-5 text-left shadow-sm">
                {creationSteps.map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-ui-hover">
                      <span className="material-icons text-[18px] text-brand-primary">{step.icon}</span>
                    </div>
                    <span className="text-[13px] text-theme-fg">{step.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-screen flex-col overflow-y-auto bg-theme-background text-theme-fg">
        <OnboardingHeader activeTheme={activeTheme} onToggleTheme={handleThemeToggle} />

        <div className="mx-auto flex w-full max-w-[1100px] flex-col items-center gap-8 px-4 py-10 sm:px-6">
          {/* Top badge */}
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-primary/30 bg-brand-primary/10 px-4 py-1.5 text-[12px] font-semibold text-brand-primary">
            <span className="material-icons text-[14px]">groups</span>
            Build Your AI Team
          </span>

          {/* Heading */}
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-[28px] font-bold leading-tight text-theme-fg sm:text-[36px]">
              Hire <span className="text-brand-primary">AI Agents</span> That Work 24/7
            </h1>
            <p className="max-w-lg text-[15px] text-theme-fg-muted">
              Specialized AI employees to automate, engage, and grow your business.
            </p>
          </div>

          {/* Agent cards */}
          <div className="grid w-full gap-5 sm:grid-cols-2 lg:grid-cols-3">

            {/* ── AI Marketer ───────────────────────────────────── */}
            <div
              className="group relative flex flex-col overflow-hidden rounded-2xl border-2 border-brand-primary shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(47,73,65,0.18)]"
              style={{ background: 'linear-gradient(150deg, rgba(47,73,65,0.11) 0%, rgba(235,245,241,0.55) 45%, #ffffff 100%)' }}
            >
              {/* Decorative blobs */}
              <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full" style={{ background: 'radial-gradient(circle, rgba(47,73,65,0.32) 0%, transparent 70%)', filter: 'blur(38px)' }} />
              <div className="pointer-events-none absolute -bottom-10 -left-10 h-36 w-36 rounded-full" style={{ background: 'radial-gradient(circle, rgba(47,73,65,0.18) 0%, transparent 70%)', filter: 'blur(28px)' }} />
              {/* Header */}
              <div className="flex flex-col items-center gap-1 px-5 pb-3 pt-6">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-primary/10 transition-transform duration-300 group-hover:scale-110">
                  <span className="material-icons text-[36px] text-brand-primary">smart_toy</span>
                </div>
                <h3 className="mt-1 text-[18px] font-bold text-theme-fg">AI Social Media Manager </h3>
                <p className="text-[13px] text-brand-primary">Grow your audience on autopilot.</p>
              </div>  

              {/* Checklist */}
              <ul className="flex flex-1 flex-col gap-1.5 px-5 pt-4">
                {[
                  "Creates posts & captions for all platforms",
                  "Generates stunning AI images",
                  "Schedules & publishes content",
                  "Creates video scripts (Reels, Shorts & more)",
                  "Generates custom UGC-style videos with your branding",
                  "Adapts tone & format per platform",
                  "Tracks reach, engagement & post performance",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-[12px]">
                    <span className="material-icons shrink-0 text-[15px] text-brand-primary">check_circle</span>
                    <span className="font-semibold text-theme-fg">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Works with */}
              <div className="px-5 pb-2 pt-3">
                <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">Works with</h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Instagram", icon: "photo_camera", color: "#E1306C" },
                    { name: "LinkedIn", icon: "work", color: "#0A66C2" },
                    { name: "Facebook", icon: "facebook", color: "#1877F2" },
                    { name: "YouTube", icon: "play_circle", color: "#FF0000" },
                    { name: "Google My Business", icon: "business", color: "#4285F4" },
                  ].map((p) => (
                    <span key={p.name} className="inline-flex items-center gap-1.5 rounded-full border border-theme-border bg-theme-background px-2.5 py-1 text-[11px] font-medium text-theme-fg-muted">
                      <span className="material-icons text-[13px]" style={{ color: p.color }}>{p.icon}</span>
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="p-5 pt-3">
                <button
                  type="button"
                  onClick={() => handleAgentSelect("social_media_manager")}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-[14px] font-semibold text-white shadow-sm transition hover:bg-brand-primary-hover active:scale-[0.98]"
                >
                  Build Social Media Manager AI
                  <span className="material-icons text-[16px]">add</span>
                  <span className="material-icons text-[16px] transition-transform duration-200 group-hover:translate-x-1">arrow_forward</span>
                </button>
              </div>
            </div>

            {/* ── AI Receptionist ────────────────────────────────── */}
            <div
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-theme-border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-primary hover:shadow-[0_24px_60px_rgba(47,73,65,0.18)]"
              style={{ background: 'linear-gradient(150deg, rgba(47,73,65,0.08) 0%, rgba(235,245,241,0.50) 45%, #ffffff 100%)' }}
            >
              {/* Decorative blobs */}
              <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full" style={{ background: 'radial-gradient(circle, rgba(47,73,65,0.28) 0%, transparent 70%)', filter: 'blur(38px)' }} />
              <div className="pointer-events-none absolute -bottom-10 -left-10 h-36 w-36 rounded-full" style={{ background: 'radial-gradient(circle, rgba(47,73,65,0.16) 0%, transparent 70%)', filter: 'blur(28px)' }} />

              {/* Header */}
              <div className="flex flex-col items-center gap-1 px-5 pb-3 pt-6">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-primary/10 transition-transform duration-300 group-hover:scale-110">
                  <span className="material-icons text-[36px] text-brand-primary">smart_toy</span>
                </div>
                <h3 className="mt-1 text-[18px] font-bold text-theme-fg">AI Receptionist</h3>
                <p className="text-[13px] text-brand-primary">Never miss a booking again.</p>
              </div>

              {/* Checklist */}
              <ul className="flex flex-1 flex-col gap-1.5 px-5 pt-4">
                {[
                  "Answers customer questions instantly",
                  "Sends booking link when ready to schedule",
                  "Follows up with leads automatically",
                  "Responds via Email & SMS",
                  "Powered by GoHighLevel CRM",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-[12px]">
                    <span className="material-icons shrink-0 text-[15px] text-brand-primary">check_circle</span>
                    <span className="font-semibold text-theme-fg">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Integrates with */}
              <div className="px-5 pb-2 pt-3">
                <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">Integrates with</h4>
                <div className="flex items-center gap-3 rounded-lg border border-theme-border bg-theme-background px-3 py-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-primary/10 text-[10px] font-extrabold text-brand-primary">GHL</span>
                  <div>
                    <p className="text-[12px] font-semibold text-theme-fg">GoHighLevel CRM</p>
                    <p className="text-[10px] text-theme-fg-muted">+ dedicated phone number</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="p-5 pt-3">
                <button
                  type="button"
                  onClick={() => handleAgentSelect("booking_agent")}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-[14px] font-semibold text-white shadow-sm transition hover:bg-brand-primary-hover active:scale-[0.98]"
                >
                  Build AI Receptionist
                  <span className="material-icons text-[16px]">add</span>
                  <span className="material-icons text-[16px] transition-transform duration-200 group-hover:translate-x-1">arrow_forward</span>
                </button>
              </div>
            </div>

            {/* ── AI Reputation Manager ──────────────────────────── */}
            <div
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-theme-border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-theme-accent-warm hover:shadow-[0_24px_60px_rgba(228,179,114,0.22)]"
              style={{ background: 'linear-gradient(150deg, rgba(228,179,114,0.16) 0%, rgba(252,246,238,0.60) 45%, #ffffff 100%)' }}
            >
              {/* Decorative blobs */}
              <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full" style={{ background: 'radial-gradient(circle, rgba(228,179,114,0.50) 0%, transparent 70%)', filter: 'blur(38px)' }} />
              <div className="pointer-events-none absolute -bottom-10 -left-10 h-36 w-36 rounded-full" style={{ background: 'radial-gradient(circle, rgba(228,179,114,0.28) 0%, transparent 70%)', filter: 'blur(28px)' }} />
              {/* Header */}
              <div className="flex flex-col items-center gap-1 px-5 pb-3 pt-6">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-accent-warm/10 transition-transform duration-300 group-hover:scale-110">
                  <span className="material-icons text-[36px] text-theme-accent-warm">smart_toy</span>
                </div>
                <h3 className="mt-1 text-[18px] font-bold text-theme-fg">AI Reputation Manager</h3>
                <p className="text-[13px] text-theme-accent-warm">Turn reviews into 5-star trust.</p>
              </div>

              {/* Checklist */}
              <ul className="flex flex-1 flex-col gap-1.5 px-5 pt-4">
                {[
                  "Monitors Google, Yelp & Facebook",
                  "AI-drafted review responses",
                  "Review request campaigns",
                  "Sentiment analysis & insights",
                  "Reputation score tracking",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-[12px]">
                    <span className="material-icons shrink-0 text-[15px] text-theme-accent-warm">check_circle</span>
                    <span className="font-semibold text-theme-fg">{item}</span>
                  </li>
                ))}
              </ul>

              {/* Monitors */}
              <div className="px-5 pb-2 pt-3">
                <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">Monitors</h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Google", icon: "search", color: "#4285F4" },
                    { name: "Yelp", icon: "rate_review", color: "#D32323" },
                    { name: "Facebook", icon: "facebook", color: "#1877F2" },
                  ].map((p) => (
                    <span key={p.name} className="inline-flex items-center gap-1.5 rounded-full border border-theme-border bg-theme-background px-2.5 py-1 text-[11px] font-medium text-theme-fg-muted">
                      <span className="material-icons text-[13px]" style={{ color: p.color }}>{p.icon}</span>
                      {p.name}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="p-5 pt-3">
                <button
                  type="button"
                  onClick={() => setShowContactModal(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-theme-accent-warm py-3 text-[14px] font-semibold text-theme-charcoal shadow-sm transition hover:brightness-110 active:scale-[0.98]"
                >
                  Build Reputation Manager
                  <span className="material-icons text-[16px]">add</span>
                  <span className="material-icons text-[16px] transition-transform duration-200 group-hover:translate-x-1">arrow_forward</span>
                </button>
              </div>
            </div>
          </div>

          {/* Reputation Manager setup request modal (SGL-141 companion) */}
          {showContactModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={closeContactModal}>
              <div
                className="w-full max-w-md overflow-hidden rounded-2xl border border-theme-border bg-theme-surface shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close */}
                <div className="flex justify-end px-4 pt-4">
                  <button
                    type="button"
                    onClick={closeContactModal}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-theme-fg-muted transition hover:bg-surface-ui-hover hover:text-theme-fg"
                  >
                    <span className="material-icons text-[20px]">close</span>
                  </button>
                </div>

                {ghlAnswer === null ? (
                  <>
                    {/* Question step — tells the team which path to route this customer down */}
                    <div className="flex flex-col items-center gap-3 px-8 pb-2 pt-2 text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-accent-warm/15">
                        <span className="material-icons text-[36px] text-theme-accent-warm">smart_toy</span>
                      </div>
                      <h3 className="text-[18px] font-bold text-theme-fg">One quick question</h3>
                      <p className="text-[13px] leading-relaxed text-theme-fg-muted">
                        AI Reputation Manager runs through GoHighLevel. Do you already have your own GoHighLevel account?
                      </p>
                    </div>
                    <div className="flex flex-col gap-2.5 px-6 pb-6 pt-5">
                      <button
                        type="button"
                        onClick={() => handleGhlAnswer(true)}
                        className="w-full rounded-xl border border-theme-border bg-theme-background px-6 py-3 text-[14px] font-semibold text-theme-fg shadow-sm transition hover:border-theme-accent-warm active:scale-[0.98]"
                      >
                        Yes, I have my own GoHighLevel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleGhlAnswer(false)}
                        className="w-full rounded-xl bg-brand-primary px-6 py-3 text-[14px] font-semibold text-white shadow-sm transition hover:bg-brand-primary-hover active:scale-[0.98]"
                      >
                        No — set one up for me (+$50/mo)
                      </button>
                    </div>
                  </>
                ) : ghlRequestStatus === "error" ? (
                  <div className="flex flex-col items-center gap-3 px-8 pb-8 pt-2 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10">
                      <span className="material-icons text-[36px] text-red-500">error_outline</span>
                    </div>
                    <h3 className="text-[18px] font-bold text-theme-fg">Couldn't send that</h3>
                    <p className="text-[13px] leading-relaxed text-theme-fg-muted">
                      Something went wrong sending your request. Please email <span className="font-semibold text-theme-fg">support@sagelyn.ai</span> directly and mention AI Reputation Manager.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Icon + heading */}
                    <div className="flex flex-col items-center gap-3 px-8 pb-2 pt-2 text-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-accent-warm/15">
                        <span className="material-icons text-[36px] text-theme-accent-warm">verified</span>
                      </div>
                      <h3 className="text-[18px] font-bold text-theme-fg">
                        {ghlRequestStatus === "sending" ? "Sending your request…" : "You're on the list!"}
                      </h3>
                      <p className="text-[13px] leading-relaxed text-theme-fg-muted">
                        {ghlAnswer === "yes"
                          ? "We'll reach out to connect your existing GoHighLevel account to your "
                          : "We'll reach out to set up a GoHighLevel account for you and connect your "}
                        <span className="font-semibold text-theme-fg">AI Reputation Manager</span> — no further action needed from you.
                      </p>
                    </div>

                    {/* Steps */}
                    <div className="mx-6 my-5 flex flex-col gap-3 rounded-xl border border-theme-border bg-theme-background p-4">
                      {[
                        { icon: "check_circle", label: "Request received by our team", done: ghlRequestStatus === "sent" },
                        {
                          icon: "settings_suggest",
                          label: ghlAnswer === "yes" ? "Our team connects your GoHighLevel account" : "Our team sets up a GoHighLevel account for you",
                          done: false,
                        },
                        { icon: "rocket_launch", label: "Your Reputation Manager goes live", done: false },
                      ].map((step, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className={`material-icons text-[18px] shrink-0 ${step.done ? "text-emerald-500" : "text-theme-fg-muted"}`}>
                            {step.icon}
                          </span>
                          <span className={`text-[12px] font-medium ${step.done ? "text-theme-fg" : "text-theme-fg-muted"}`}>
                            {step.label}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="px-6 pb-6">
                      <button
                        type="button"
                        onClick={closeContactModal}
                        disabled={ghlRequestStatus === "sending"}
                        className="w-full rounded-xl bg-brand-primary px-6 py-3 text-[14px] font-semibold text-white shadow-sm transition hover:bg-brand-primary-hover active:scale-[0.98] disabled:opacity-50"
                      >
                        {ghlRequestStatus === "sending" ? "Sending…" : "Got it, let's finish setup"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {agentCreationError && (
            <p className="text-center text-[13px] text-amber-600">{agentCreationError}</p>
          )}

          {/* Configure later */}
          <button
            type="button"
            onClick={() => { markOnboardingComplete(); navigateAfterOnboarding(); }}
            className="flex items-center gap-2 rounded-xl border border-theme-border bg-theme-surface px-6 py-3 text-[13px] font-medium text-theme-fg-muted shadow-sm transition hover:bg-surface-ui-hover hover:text-theme-fg"
          >
            <span className="material-icons text-[16px]">settings</span>
            Configure agents later
            <span className="material-icons text-[14px]">chevron_right</span>
          </button>
        </div>
      </div>
    );
  }

  if (onboardingStep === "reveal") {
    const businessName = editedProfile?.business_name || "Your Business";
    const isTraining = trainingStatus === "running";
    const trainingDone = trainingStatus === "done";
    const revealAgentType = Object.values(AGENT_CONFIGS).find(
      (c) => c.agent_name === selectedAgentTitle
    )?.agent_type;
    return (
      <div className="flex h-screen flex-col overflow-y-auto bg-theme-background text-theme-fg">
        <OnboardingHeader activeTheme={activeTheme} onToggleTheme={handleThemeToggle} />
        <div className="flex flex-1 items-center justify-center px-4 py-8">
          <div className="flex w-full max-w-md animate-in fade-in slide-in-from-bottom-6 flex-col items-center gap-6 text-center duration-700">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-primary shadow-lg">
              <span className="material-icons text-[40px] text-theme-accent-warm">auto_awesome</span>
            </div>
            <div className="flex flex-col gap-2">
              <h1 className="font-serif text-3xl font-bold text-theme-fg">You're all set!</h1>
              <p className="text-[15px] text-theme-fg-muted">
                Your <span className="font-semibold text-theme-fg">{selectedAgentTitle || "AI assistant"}</span> for{" "}
                <span className="font-semibold text-theme-fg">{businessName}</span> is ready.
              </p>
            </div>

            {/* Training status card */}
            {editedProfile?.website_url && (
              <div className="w-full rounded-2xl border border-theme-border bg-theme-surface p-4 text-left transition-colors">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-ui-hover">
                    <span
                      className={`material-icons text-[18px] ${
                        trainingDone
                          ? "text-theme-accent-warm"
                          : isTraining
                          ? "animate-spin text-brand-primary"
                          : "text-theme-fg-muted"
                      }`}
                    >
                      {trainingDone ? "library_books" : isTraining ? "sync" : "school"}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-theme-fg">
                      {trainingDone
                        ? `Learned from ${crawlProgress?.pagesCompleted ?? "several"} pages`
                        : isTraining
                        ? "Training your AI on your website..."
                        : "AI training queued"}
                    </p>
                    <p className="mt-0.5 text-[12px] text-theme-fg-muted">
                      {trainingDone
                        ? "Your AI now knows your services, pricing, and more"
                        : isTraining
                        ? `This takes 1-2 minutes. ${crawlProgress ? `${crawlProgress.pagesCompleted}/${crawlProgress.pagesTotal} pages read` : "Starting..."}`
                        : "Will complete in the background"}
                    </p>
                  </div>
                </div>
                {isTraining && crawlProgress && crawlProgress.pagesTotal > 0 && (
                  <div className="mt-3">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-ui-hover">
                      <div
                        className="h-full rounded-full bg-brand-primary transition-all duration-700"
                        style={{ width: `${Math.min(100, Math.round((crawlProgress.pagesCompleted / crawlProgress.pagesTotal) * 100))}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <AgentConnectionPrompt agentType={revealAgentType} variant="card" />

            <div className="flex w-full flex-col gap-3 pt-1">
              <button
                type="button"
                onClick={() => {
                  markOnboardingComplete();
                  navigateAfterOnboarding();
                }}
                className="w-full rounded-xl bg-brand-primary px-6 py-3.5 text-[15px] font-semibold text-white shadow-sm transition hover:bg-brand-primary-hover active:scale-[0.98]"
              >
                {selectedAgentTitle?.toLowerCase().includes("social")
                  ? "Start creating social media content"
                  : "Start chatting with your AI"}
              </button>
              {isTraining && (
                <p className="text-[12px] text-theme-fg-muted">
                  Training continues in the background — you can start using your AI now.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // "I'll do it manually": the plain form, full screen, pre-filled with
  // anything the assistant already found. Never a redirect: /customer bounces
  // a Brand-less customer straight back here (SGL-448).
  if (quickFormPrefill !== null) {
    return (
      <div className="relative flex h-screen flex-col overflow-hidden bg-theme-background text-theme-fg">
        <OnboardingHeader
          activeTheme={activeTheme}
          onToggleTheme={handleThemeToggle}
          onBack={() => setQuickFormPrefill(null)}
        />
        <div className="flex flex-1 justify-center overflow-y-auto px-4 py-8">
          <QuickStartForm
            initial={quickFormPrefill}
            submitting={quickSubmitting}
            error={quickError}
            onSubmit={handleQuickStart}
          />
        </div>
      </div>
    );
  }

  // Chat-first setup: the assistant greets the owner and asks a few questions,
  // like any agent chat. "Ask a question" and the other entry actions still
  // reach the free-form assistant below.
  if (entryMode === "account" && !assistantMode && !hasUserMessages && !businessProfile) {
    return (
      <div className="relative flex h-screen flex-col overflow-hidden bg-theme-background text-theme-fg">
        <OnboardingHeader
          activeTheme={activeTheme}
          onToggleTheme={handleThemeToggle}
          onBack={() => navigate("/")}
          onManual={openQuickForm}
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-6 sm:px-6">
          {/* Before the first message: greeting, options and input as one group,
              centred at any screen size. After it: thread from the top, input docked. */}
          <div
            className={`${CHAT_COLUMN_CLASS} flex flex-col gap-8 ${
              setupStarted ? "" : "min-h-full justify-center pb-[8vh]"
            }`}
          >
            {/* Wait for the User so the greeting has their name, not "there". */}
            {!userLoading && idToken && resume !== undefined && <AssistantSetupChat
              firstName={firstName}
              avatar={<AssistantAvatar size="md" />}
              idToken={idToken}
              agentId={ONBOARDING_AGENT_ID}
              customerId={ONBOARDING_CUSTOMER_ID}
              provider={onboardingChatProvider}
              key={resume ? `resume-${resume.brand.customerId}` : "new"}
              resume={resume ?? undefined}
              onCreateBrand={createBrandFromAnswers}
              onSaveDetails={saveDetailsFromAnswers}
              onUseForm={(v) => {
                setQuickError(null);
                setQuickFormPrefill(v);
              }}
              onFinish={finishOnboarding}
              onStarted={setSetupStarted}
              onOtherAction={handleActionButton}
            />}

          </div>
        </div>
        <SetupComposerDock />
      </div>
    );
  }

  return (
    <div className="relative flex h-screen flex-col overflow-hidden bg-theme-background text-theme-fg">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <OnboardingHeader
        activeTheme={activeTheme}
        onToggleTheme={handleThemeToggle}
        onBack={() => navigate("/")}
        onManual={openQuickForm}
      />

      {/* ── Message thread ──────────────────────────────────────────── */}
      <div className={`relative flex-1 overflow-y-auto px-4 ${showWelcome ? "py-0" : "py-8"}`}>
        <div
          className={`mx-auto flex max-w-3xl flex-col gap-6 ${
            showWelcome ? "min-h-full max-w-4xl items-center justify-center pb-24 pt-10" : ""
          }`}
        >
          {messages.map((msg, i) => {
            const isAssistant = msg.role === "assistant";
            const isFirst = i === 0;

            // First greeting — hero layout before any user interaction
            if (isFirst && isAssistant && !hasUserMessages) {
              const lines = msg.content.split("\n\n");
              const headline = lines[0] ?? msg.content;
              const subtitle = lines[1] ?? "";
              return (
                <div key={i} className="flex w-full flex-col items-center gap-9 text-center"
                  style={{ animation: "onboardingFadeUp 0.6s ease both" }}>
                  <div className="mb-2 flex max-w-2xl flex-col items-center gap-3">
                    <p className="text-[30px] font-semibold leading-snug text-theme-fg sm:text-[32px]">{headline}</p>
                    {subtitle && <p className="max-w-[620px] text-[15px] leading-relaxed text-theme-fg-muted">{subtitle}</p>}
                  </div>
                  {!isStreaming && (
                    <div className="grid w-full max-w-[900px] grid-cols-2 gap-3 sm:grid-cols-4">
                      {ACTION_BUTTONS.map((ab) => (
                        <OnboardingQuickReplyButton
                          key={ab.key}
                          label={ab.label}
                          description={ab.description}
                          icon={ab.icon}
                          active={ab.key === entryMode}
                          onClick={() => handleActionButton(ab.key)}
                        />
                      ))}
                    </div>
                  )}
                  {entryMode === "account" && !assistantMode ? (
                    <QuickStartForm
                      submitting={quickSubmitting}
                      error={quickError}
                      onSubmit={handleQuickStart}
                      onUseAssistant={() => setAssistantMode(true)}
                    />
                  ) : (
                  <div className="flex w-full max-w-[590px] flex-col gap-2">
                    <OnboardingComposer
                      input={input}
                      setInput={setInput}
                      inputRef={inputRef}
                      isStreaming={isStreaming}
                      hasUserMessages={hasUserMessages}
                      onSubmit={handleSubmit}
                      variant="hero"
                      placeholder={
                        entryMode === "support"
                          ? "Describe the problem you're running into..."
                          : entryMode === "ask"
                            ? "Ask me anything about Sagelyn..."
                            : undefined
                      }
                    />
                    {entryMode === "account" && <OnboardingWebsiteCue />}
                  </div>
                  )}
                </div>
              );
            }

            return (
              <SetupThread
                key={i}
                bubbles={[{ from: msg.role, text: msg.content, steps: msg.steps }]}
                avatar={(i === 0 || messages[i - 1].role !== "assistant") ? <AssistantAvatar size="md" /> : null}
              />
            );
          })}

          {/* Tool / Loading indicator */}
          {isStreaming && (
            <div className="flex w-full justify-start">
              <div className="flex w-full max-w-2xl flex-col gap-1">

                <LiveAssistantTurn
                  avatar={<AssistantAvatar size="md" />}
                  showHeader={messages.length === 0 || messages[messages.length - 1].role !== "assistant"}
                  steps={liveSteps}
                  streaming={streamingContent}
                />
                {/* Crawl live progress: real page counts from the crawl stream */}
                {streamStatus.type === "tool" && streamStatus.name.includes("crawl") && crawlProgress && (
                  <div className="flex flex-col gap-3 pl-[54px]">
                    <div className="flex items-center gap-3">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-ui-hover">
                        <div
                          className="h-full rounded-full bg-theme-primary transition-all duration-500"
                          style={{ width: `${Math.min(100, crawlProgress.pagesTotal > 0 ? (crawlProgress.pagesCompleted / crawlProgress.pagesTotal) * 100 : 0)}%` }}
                        />
                      </div>
                      <span className="shrink-0 text-[12px] tabular-nums text-theme-fg-muted">
                        {crawlProgress.pagesCompleted} / {crawlProgress.pagesTotal} pages
                      </span>
                    </div>
                    {crawlPageTitles.length > 0 && (
                      <div className="flex max-h-[120px] flex-col gap-1 overflow-y-auto">
                        {[...crawlPageTitles].reverse().slice(0, 6).map((title, ti) => (
                          <div key={ti} className="flex items-center gap-2 text-[12px] text-theme-fg-muted">
                            <span className="material-icons text-[14px] text-theme-primary">check</span>
                            <span className="truncate">{title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase 3 — Crawl completed teaser card */}
          {crawlProgress?.status === "completed" && crawlPageTitles.length > 0 && (
            <div className="mx-auto mt-2 w-full max-w-2xl overflow-hidden rounded-2xl border border-[#d4e8df] bg-[#f0f7f4] p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#183b32]">
                  <span className="material-icons text-[18px] text-[#e4b372]">auto_awesome</span>
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-[#183b32]">
                    Your AI just learned from {crawlProgress.pagesCompleted} pages
                  </p>
                  <p className="text-[12px] text-[#648b7f]">It now knows your services, FAQs, and brand voice</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {crawlPageTitles.slice(0, 5).map((title, i) => (
                  <span key={i} className="rounded-full border border-[#b8d8cb] bg-white px-3 py-1 text-[12px] font-medium text-[#183b32]">
                    {title}
                  </span>
                ))}
                {crawlPageTitles.length > 5 && (
                  <span className="rounded-full border border-[#b8d8cb] bg-white px-3 py-1 text-[12px] font-medium text-[#648b7f]">
                    +{crawlPageTitles.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Business profile confirmation form */}
          {businessProfile && editedProfile && (() => {
            const profileFields: Array<{
              label: string;
              value: string;
              icon: string;
              field: keyof BusinessProfile;
              required: boolean;
            }> = [
              { label: "Business Name", value: editedProfile.business_name, icon: "business", field: "business_name", required: true },
              { label: "City", value: editedProfile.city, icon: "location_city", field: "city", required: true },
              { label: "State / Region", value: editedProfile.state, icon: "map", field: "state", required: true },
              { label: "Country", value: editedProfile.country, icon: "public", field: "country", required: true },
            ];
            const missingRequired = profileFields.filter((f) => f.required && !f.value.trim());
            const profileIsValid = missingRequired.length === 0;

            return (
              <div className="mx-auto mt-6 w-full max-w-2xl overflow-hidden rounded-3xl border border-theme-border bg-theme-surface p-8 text-theme-fg shadow-sm">
                <div className="mb-6 flex items-start justify-between gap-3">
                  <h3 className="text-xl font-bold text-theme-fg">Here's what I found</h3>
                  {!profileIsValid && (
                    <span className="shrink-0 rounded-full border border-theme-border bg-theme-background px-3 py-1 text-xs font-medium text-theme-fg-muted">
                      {missingRequired.length} required field{missingRequired.length > 1 ? "s" : ""} missing
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  {profileFields.map((item) => {
                    const isEmpty = item.required && !item.value.trim();
                    return (
                      <div
                        key={item.field}
                        className={`flex items-start py-2.5 transition-colors ${isEmpty ? "rounded-xl border border-theme-border bg-theme-background px-3" : ""}`}
                      >
                        <div className="flex w-full items-center gap-4">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-ui-hover">
                            <span className={`material-icons text-[20px] ${isEmpty ? "text-theme-accent-warm" : "text-brand-primary"}`}>{item.icon}</span>
                          </div>
                          <div className="min-w-[140px]">
                            <span className="text-[15px] font-semibold text-theme-fg">{item.label}</span>
                            {item.required && (
                              <span className="ml-1 text-theme-accent-warm">*</span>
                            )}
                            {isEmpty && (
                              <p className="text-[11px] text-theme-fg-muted">Required — click Edit to add</p>
                            )}
                          </div>

                          {editingField === item.field ? (
                            <div className="flex flex-1 items-center gap-3 pr-2">
                              <input
                                type="text"
                                autoFocus
                                value={item.value}
                                onChange={(e) => patchProfile({ [item.field]: e.target.value })}
                                onBlur={() => setEditingField(null)}
                                onKeyDown={(e) => { if (e.key === "Enter") setEditingField(null); }}
                                className="w-full border-b-2 border-brand-primary bg-transparent py-1 text-[15px] text-theme-fg outline-none"
                              />
                              <button
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white hover:bg-brand-primary-hover"
                                onClick={() => setEditingField(null)}
                              >
                                <span className="material-icons text-[16px]">check</span>
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-1 items-center justify-between gap-4">
                              <span className={`truncate text-[15px] ${item.value ? "text-theme-fg-muted" : "italic text-theme-fg-muted"}`}>
                                {item.value || "Not provided"}
                              </span>
                              <button
                                className={`flex items-center gap-1.5 text-[15px] font-semibold transition-colors hover:text-theme-fg ${isEmpty ? "text-theme-accent-warm" : "text-brand-primary"}`}
                                onClick={() => setEditingField(item.field)}
                              >
                                <span className="material-icons text-[18px]">edit</span> Edit
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!profileIsValid && (
                  <p className="mt-4 text-[13px] text-theme-fg-muted">
                    <span className="text-theme-accent-warm">*</span> Required fields must be filled before continuing.
                  </p>
                )}

                {confirmError && (
                  <div className="mt-4">
                    <p className="rounded-xl border border-red-200 bg-red-50 p-4 text-[15px] text-red-600">
                      {confirmError}
                    </p>
                  </div>
                )}

                <div className="mt-8 flex flex-col gap-3">
                  <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <span className="text-[15px] font-medium text-theme-fg">Does everything look right?</span>
                    <div className="flex w-full gap-2.5 sm:w-auto">
                      <button
                        type="button"
                        disabled={isConfirming}
                        onClick={handleRetry}
                        className="flex-1 rounded-xl border border-theme-border bg-theme-surface px-4 py-2.5 text-[14px] font-medium text-theme-fg shadow-sm transition hover:bg-surface-ui-hover disabled:opacity-50 sm:flex-none"
                      >
                        Something's not right
                      </button>
                      <button
                        type="button"
                        disabled={isConfirming || !profileIsValid}
                        onClick={handleConfirm}
                        className="flex-1 rounded-xl bg-brand-primary px-6 py-2.5 text-[14px] font-medium text-white shadow-sm transition hover:bg-brand-primary-hover active:scale-[0.98] disabled:opacity-50 sm:flex-none"
                      >
                        {isConfirming ? "Setting up..." : "Yes, this looks correct"}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 before:h-px before:flex-1 before:bg-theme-border after:h-px after:flex-1 after:bg-theme-border">
                    <span className="text-[12px] font-medium text-theme-fg-muted">or</span>
                  </div>
                  <button
                    type="button"
                    onClick={openQuickForm}
                    className="group flex w-full items-center justify-center gap-2 rounded-xl border border-theme-border bg-theme-surface px-5 py-2.5 text-[14px] font-medium text-theme-fg shadow-sm transition hover:border-brand-primary hover:bg-surface-ui-hover active:scale-[0.98]"
                  >
                    <span className="material-icons text-[18px] text-theme-fg-muted transition group-hover:text-brand-primary">edit_note</span>
                    I'll do it manually
                  </button>
                </div>
              </div>
            );
          })()}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {showWelcome && (
        <div className="pointer-events-none absolute bottom-7 left-0 right-0 px-4">
          <OnboardingSecureNote />
        </div>
      )}

      {/* ── Input bar ───────────────────────────────────────────────── */}
      {!businessProfile && !showWelcome && (
        <div
          className={`relative shrink-0 bg-transparent px-4 pb-8 pt-2 transition-all duration-500 ease-out ${
            chatTransitionActive ? "translate-y-0 opacity-100" : "-translate-y-8 opacity-0"
          }`}
        >
          <OnboardingComposer
            input={input}
            setInput={setInput}
            inputRef={inputRef}
            isStreaming={isStreaming}
            hasUserMessages={hasUserMessages}
            onSubmit={handleSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default OnboardingAgentPage;
